<template>
 <v-list two-line subheader id='chapter-markers'>
   <v-list-tile class="py-2"
     v-for="marker in chapterMarkers"
     :key="marker.id"
     avatar
     @click="jumpToInteraction(marker.displayInTime, marker.interaction)"
     tabindex="0"
     @keyup.enter="jumpToInteraction(marker.displayInTime, marker.interaction)"
     :disabled="isLiveSession"
   >
     <v-list-tile-avatar>
       <v-icon
         class="grey lighten-1 white--text"
         :class="{'darken-1':marker.type === 'video'}"
         v-text="marker.icon"
       ></v-icon>
     </v-list-tile-avatar>

     <v-list-tile-content>
       <v-list-tile-title v-text="marker.title"></v-list-tile-title>
       <v-list-tile-sub-title class="lighten-2" v-text="marker.duration"></v-list-tile-sub-title>
     </v-list-tile-content>
     <v-list-tile-action>
       <interaction-score
        v-if="(
          marker.interactionMeta
          && marker.interactionMeta.isInteractionAnswered
        )"
        :interaction='marker.interaction'
        :interactionAttempt="marker.interactionAttempt"
        :interactionMeta="marker.interactionMeta">
      </interaction-score>
     </v-list-tile-action>
   </v-list-tile>

 </v-list>
 </template>

 <script>
 /* global _, dataLayer */
 /* global moment */
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import EventBus from '@/buses/EventBus';
import InteractionScore from '@/components/interaction/InteractionScore';
import _ from 'lodash';
import sanitizeHtml from 'sanitize-html';

export default {
  name: 'chapter-markers',
  components: {
    InteractionScore
  },
  computed: {
    ...mapGetters('player', [
      'bulbVideos',
      'displayTime',
      'maxTime',
    ]),
    ...mapState('player', [
      'formatedDuration',
      'isLiveSession',
      'sessionId',
    ]),
    ...mapGetters('interactions', [
      'icons',
      'interactionStates',
      'types',
    ]),
    /**
     * This computed property hurts my brain.
     * 
     * In essence, it is dynamically building up the chapter markers to render in the player's sidebar.
     */
    chapterMarkers() {
      return this.interactionStates.concat(this.bulbVideos).map((marker, i) => {
        if (_.has(marker, 'id')) {
          // format bulbVideo
          return {
            id: `bv-${marker.id}`,
            displayInTime: marker.displayStart,
            duration: this.formatChapterDuration(marker.displayStart, marker.displayEnd),
            icon: this.icons['secondary_stream'], // really this is a primary stream but we use the same icon
            title: marker.name || marker.video.name,
            type: 'video',
          }
         }
        else if (_.has(marker, 'interaction.id')) {
          // remove secondary stream
          if (marker.interaction.interactionType === 'secondary_stream') return null;
            // format interaction
            return {
              id: `i-${marker.interaction.id}`,
              displayInTime: marker.interaction.displayInTime,
              duration: this.formatChapterDuration(marker.interaction.displayInTime, marker.interaction.displayOutTime),
              icon: this.icons[marker.interaction.interactionType],
              title: this.formatChapterTitle(marker.interaction.interactionText, this.types[marker.interaction.interactionType]),
              type: 'interaction',
              interaction: marker.interaction,
              interactionAttempt: marker.interactionAttempt,
              interactionMeta: marker.interactionMeta,
           }
         }
        dataLayer.errorLog({
          msg: `err generating chapter marker, sessionId: ${this.sessionId}`,
          location: 'ChapterMarkers',
          status: marker,
          label:'ChapterMarkers'
        });
        return null;
      })
      .filter(marker => {
        return (
          _.has(marker, 'id')
          && (
            marker.displayInTime <= (this.maxTime + .500) // Adding variance in here to accomodate PPWV-1414
            && !_.get(marker, 'interactionMeta.hiddenByRequired', false)
          )
        )
      })
      .sort((m1, m2) => {
        if (m1.displayInTime === m2.displayInTime) {
          // interaction time and video time are equal
          // compare modify displayInTime, forcing bulbVideo before interaction
          // add one to account for displayInTime ===  0
          const mk1 = (1 + m1.displayInTime) * (m1.type === 'video' ? 1 : 100);
          const mk2 = (1 + m2.displayInTime) * (m2.type === 'video' ? 1 : 100);
          return (mk1 - mk2);
        }
        return (m1.displayInTime - m2.displayInTime);
      });
    },
  },
  methods: {
    ...mapMutations('player', [
      'setDesiredState',
    ]),
    ...mapActions('interactions', [
      'requestSetActiveInteraction',
    ]),
    formatChapterDuration(inTime, outTime) {
      if (outTime > inTime) {
        return `${this.formatedDuration(inTime)} - ${this.formatedDuration(outTime)}`
      }
      return this.formatedDuration(inTime);
    },
    formatChapterTitle(text, defaultText = 'Interaction') {
      const formatedText = sanitizeHtml(text, {
        allowedTags: [],
        allowedAttributes: {
          iframe: [ 'src'],
          a: [ 'href', 'name', 'target' ],
          img: [ 'src', 'alt' ],
          audio: ['src']
        },
      }).replace(/\s\s+/g, ' ')
      .replace(/&amp;/g, "&");
      if (formatedText.trim() === '') {
        return defaultText;
      }
      return formatedText;
    },
    jumpToInteraction(displayTime, interaction) {
      this.setDesiredState('pause');
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: displayTime,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
      setTimeout(() => {
        /*
          This is to prevent trying to set an active interaction outside the current displayTime
          Specifically when a bulb has rewind_yes = 0 and you attempt to jump back to a previous interaction while
          on an interaction.  While the clicked on interaction won't be set, it did hide the required interaction
          making it appear that the Player was stuck.
        */
        if (this.displayTime - interaction?.displayInTime < .5) this.requestSetActiveInteraction(interaction);
      }, 5);
    },
  },
 };
 </script>

<style scoped>
 #chapter-markers {
   width: 100%;
 }
 #chapter-markers .v-list__tile .v-list__tile__title {
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
 }
</style>
